import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PreviewDrawer } from "@ippon/shared-components";
import PaymentDetailPage from "@/pages/Payments/Detail";

interface Props {
  open: boolean;
  onClose: () => void;
  paymentId: string | null;
  /** Payment は Unit (section) で partition されているため必須 */
  propertySectionId: string;
  /** パンくず構築のための物件 ID (全画面遷移時に URL クエリへ付与) */
  propertyId?: string;
  /** パンくず表示用 物件名 */
  propertyName?: string;
  /** パンくず表示用 Unit 名 (テナント管理タブの上位ノードに使う) */
  unitName?: string;
  /**
   * 全画面遷移時のパンくず起点。既定 "payments" (テナント管理→入金管理)。
   * 収入サブタブからは "income" を渡し、収支→収入のパンくずにする。
   */
  fromSource?: string;
  /** 編集成功時のコールバック (親で一覧再取得) */
  onUpdated?: (paymentId: string) => void;
}

/** Notion 風プレビュードロワー: 入金詳細ページをそのまま埋め込む */
const PaymentDetailDrawer: React.FC<Props> = ({
  open,
  onClose,
  paymentId,
  propertySectionId,
  propertyId,
  propertyName,
  unitName,
  fromSource = "payments",
  onUpdated,
}) => {
  const navigate = useNavigate();
  const [loadedTitle, setLoadedTitle] = useState<string | null>(null);

  useEffect(() => {
    setLoadedTitle(null);
  }, [paymentId]);

  const handleLoaded = (rec: any) => {
    const month = rec?.aggregation_month;
    if (month && /^\d{4}-\d{2}$/.test(month)) {
      setLoadedTitle(`${month.slice(0, 4)}年${month.slice(5, 7)}月`);
    } else {
      setLoadedTitle(null);
    }
  };

  const openPage = () => {
    if (!paymentId || !propertySectionId) return;
    const qs = new URLSearchParams();
    qs.set("property_section_id", propertySectionId);
    if (propertyId) qs.set("property_id", propertyId);
    // 収入文脈はリロードでも維持されるよう query に持たせる (場所区分/名称欄の表示用)
    if (fromSource === "income") qs.set("context", "income");
    navigate(`/payments/${paymentId}?${qs.toString()}`, {
      state: {
        propName: propertyName,
        propertyId,
        privateAreaId: propertySectionId,
        privateAreaName: unitName,
        fromSource,
        title: loadedTitle,
        backPath:
          fromSource === "income" && propertyId
            ? `/properties/${propertyId}?tab=5&subtab=1`
            : undefined,
      },
    });
    onClose();
  };

  return (
    <PreviewDrawer open={open} onClose={onClose} onExpand={openPage}>
      {paymentId && propertySectionId && (
        <PaymentDetailPage
          overridePaymentId={paymentId}
          overridePropertySectionId={propertySectionId}
          overridePropertyId={propertyId}
          incomeContext={fromSource === "income"}
          embedded
          onUpdated={onUpdated}
          onLoaded={handleLoaded}
        />
      )}
    </PreviewDrawer>
  );
};

export default PaymentDetailDrawer;
